import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiRequest } from '@/features/videos-common/api/client';
import {
  buildPlaylistAudienceBody,
  buildPlaylistImportBody
} from '@/features/staff-settings-videos/api/requestBodies';
import { DEFAULT_PAGE_SIZE, pagePath, pageResults } from '@/features/staff-settings-videos/api/pagination';
import { STAFF_PLAYLISTS_KEY, STAFF_VIDEOS_KEY } from './queryKeys';

/**
 * A playlist as returned by the server (Swagger StaffPlaylist).
 *
 * @typedef {Object} StaffPlaylist
 * @property {number} id
 * @property {string} youtubePlaylistId
 * @property {string} title
 * @property {string|null} description
 * @property {boolean} isPublished
 * @property {import('@/features/videos-common/audiences').HomeVideoAudience|null} audience - null means unassigned
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * Response of GET /staff/playlists?page=&pageSize=. pageSize is 0 to 500
 * (default 100); pageSize 0 returns only `count`.
 *
 * @typedef {Object} StaffPlaylistsPage
 * @property {number} count - Total playlists across all pages
 * @property {number} page - 1-based
 * @property {number} pageSize
 * @property {boolean} next - Whether a later page has playlists
 * @property {boolean} previous - Whether page is greater than 1
 * @property {StaffPlaylist[]} results
 */

/**
 * Response of POST /staff/playlists/import-preview.
 *
 * @typedef {Object} StaffPlaylistImportPreview
 * @property {string} youtubePlaylistId
 * @property {string} playlistTitle
 * @property {import('@/features/videos-common/audiences').HomeVideoAudience|null} audience
 * @property {{ id: number, title: string }|null} audienceConflict - a different playlist holding audience
 * @property {number} itemCount
 * @property {Array<{ youtubeVideoId: string, title: string, position: number }>} newVideos
 * @property {number} existingCount
 */

/**
 * Response of POST /staff/playlists/import.
 *
 * @typedef {Object} StaffPlaylistImportResult
 * @property {StaffPlaylist} playlist
 * @property {boolean} playlistCreated
 * @property {number} createdCount
 * @property {number} existingCount
 * @property {import('@/features/staff-settings-videos/hooks/useStaffVideos').StaffVideo[]} created
 */

/**
 * 409 body (on ApiError.body) when an audience is held by a different playlist.
 *
 * @typedef {Object} AudienceConflict
 * @property {string} error
 * @property {number} statusCode
 * @property {{ id: number, title: string }} holder
 */

/**
 * GET /staff/playlists: one page of playlists, newest first, each with its
 * audience (null when unassigned). The cache holds the whole paginated
 * response (StaffPlaylistsPage); `data` is just the page's playlists.
 *
 * @param {{ enabled?: boolean, page?: number, pageSize?: number }} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<StaffPlaylist[]>}
 */
export function useStaffPlaylists(options = {}) {
  const { enabled = true, page = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  const request = useApiRequest();

  return useQuery({
    queryKey: [...STAFF_PLAYLISTS_KEY, { page, pageSize }],
    queryFn: () => request(pagePath('/staff/playlists', { page, pageSize })),
    select: pageResults,
    enabled
  });
}

/**
 * POST /staff/playlists/import-preview. Writes nothing.
 * mutate({ url, audience }) resolves to a StaffPlaylistImportPreview; its
 * audienceConflict names a different playlist holding that audience.
 */
export function usePlaylistImportPreview() {
  const request = useApiRequest();

  return useMutation({
    mutationFn: ({ url, audience = null }) => request('/staff/playlists/import-preview', {
      method: 'POST',
      body: buildPlaylistImportBody(url, audience)
    })
  });
}

/**
 * POST /staff/playlists/import. New videos arrive as unpublished drafts,
 * tagged Educator or Partner for those audiences and untagged otherwise.
 * mutate({ url, audience }) resolves to a StaffPlaylistImportResult. Returns
 * 409 when a different playlist holds the audience.
 */
export function usePlaylistImport() {
  const request = useApiRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ url, audience = null }) => request('/staff/playlists/import', {
      method: 'POST',
      body: buildPlaylistImportBody(url, audience)
    }),
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries(STAFF_VIDEOS_KEY),
      queryClient.invalidateQueries(STAFF_PLAYLISTS_KEY)
    ])
  });
}

/**
 * PATCH /staff/playlists/:id with a new audience (null unassigns).
 * mutate({ id, audience, replaceExisting }). Without replaceExisting, an
 * audience held by a different playlist fails with a 409 whose body names
 * the holder (error.body is an AudienceConflict).
 */
export function useUpdatePlaylistAudience() {
  const request = useApiRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, audience, replaceExisting = false }) => request(`/staff/playlists/${id}`, {
      method: 'PATCH',
      body: buildPlaylistAudienceBody(audience, { replaceExisting })
    }),
    onSettled: () => queryClient.invalidateQueries(STAFF_PLAYLISTS_KEY)
  });
}
